import dayJs, { Dayjs, OptionType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { shortMonthsListEn } from 'core/common/constants';
import { DateParts, MonthsIndex } from 'core/common/entities';
import { Languages } from 'core/localization/entities';
import { addLeadingZero } from './addLeadingZero';

dayJs.extend(localizedFormat);
dayJs.extend(advancedFormat);

export const createDate = (
  date?: string | number | Date | Dayjs | null | undefined,
  format?: OptionType,
  locale?: string,
  strict?: boolean,
) => {
  return dayJs(date, format, locale, strict);
};

export const formatDateByLocale = (date: string, locale: Languages) => {
  const parsedDate = createDate(date);

  if (!parsedDate.isValid()) return '';

  return new Intl.DateTimeFormat(locale || Languages.EN, {
    dateStyle: 'long',
    calendar: 'gregory',
  }).format(parsedDate.toDate());
};

export const extractDateParts = (date = '1990-01-01'): DateParts => {
  const formattedDate = createDate(date, 'MM-DD-YYYY');
  const defaultDate = {
    day: 1,
    month: 0,
    year: 1990,
  };

  const day = formattedDate.get('date') || defaultDate.day;
  const month = formattedDate.get('month') || defaultDate.month;
  const year = formattedDate.get('year') || defaultDate.year;

  return {
    day: addLeadingZero(day),
    month: String(month) as MonthsIndex,
    year: addLeadingZero(year),
  };
};

export const normalizeDate = (date: DateParts, datePattern: OptionType) => {
  const monthNumber = Number(date.month);
  const monthValue = shortMonthsListEn[monthNumber];

  const newDate = createDate(`${monthValue} ${date.day} ${date.year}`, datePattern).format(
    'YYYY-MM-DD',
  );

  if (newDate === 'Invalid Date') {
    return createDate(`Jan 01 1990`, 'MMM D YYYY').format('YYYY-MM-DD');
  }
  return newDate;
};

export const isBefore = (dateNow: number, dateToCompare: number) => {
  return dayJs(dateNow).isBefore(dayJs(dateToCompare));
};

export const getCurrentAndNextMonths = (quantity: number, monthsList: string[]): string[] => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const nextMonths = Array.from({ length: quantity }, (_, i) => (currentMonth + i) % 12);

  return nextMonths.map((month) => monthsList[month]);
};

export const isFutureDate = (date: string) => {
  const parsed = dayJs(date);
  return parsed.isValid() && parsed.isAfter(dayJs());
};
